import { useState, ChangeEvent } from 'react';
import { Box, Button, List, Typography, Checkbox, TextField } from '@mui/material/';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import { ListItem, Modal, useNotification } from '../..';
import { useSelectedItemsValidation } from '../../../hooks';
import { IModal } from '../Modal';
import { generatePath, useNavigate } from 'react-router';
import URLs from '../../../pages/URLs';
import { useSalonStore } from '../../../store';
import { useTranslation } from 'react-i18next';
import { SendActionPlanData } from '../../../types';
import { StyledNote } from './SendFormModal.styles';
import { PLAN_TYPE } from '../../../pages/ActionPlan/constants';

interface ISendFormModal extends IModal {
  plans: string[];
}

export const SendFormModal = ({ open, handleClose, plans }: ISendFormModal) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { salon, sendActionPlan, actionPlan, resetStore } = useSalonStore();

  const { selectedItems, onCheckboxClick, isMinSelected } = useSelectedItemsValidation();

  const [step, setStep] = useState<number>(1);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(false);

  const { showMessage } = useNotification();

  const payload: SendActionPlanData = {
    recipientEmail: email,
    selectedActionPlan: Object.values(selectedItems).flat(),
  };

  const handleCloseBtn = () => {
    if (step === 3) {
      closeSendForm();
    } else {
      setStep(1);
      handleClose();
    }
  };

  const closeSendForm = () => {
    handleClose();
    salon && navigate(generatePath(URLs.SalonAfterPlanSent, { salonId: salon.id, sent: 'sent' }));
  };

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value.trim());
    if (e.target.validity.valid) {
      setEmailError(false);
    } else {
      setEmailError(true);
    }
  };

  const handleSendBtn = async () => {
    try {
      const result = await sendActionPlan(payload, actionPlan.id, showMessage, navigate);
      if (result) {
        resetStore();
        setStep(3);
      } else {
        showMessage(t('sendFormModal.errorSendingPlan'), 'error');
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : t('sendFormModal.errorSendingPlan');
      showMessage(errorMessage, 'error');
    }
  };

  const header = () => (
    <>
      <Typography variant="h6" textTransform="capitalize">
        {step !== 3 ? t('sendFormModal.sendReport') : actionPlan?.title}
      </Typography>
      <Typography variant="body2">
        {step === 1 && t('sendFormModal.reportIndicate')}
        {step === 2 && t('sendFormModal.emailIndicate')}
      </Typography>
    </>
  );

  const content = () => {
    if (step === 1) {
      return (
        <List data-testid="plans-list">
          {plans.map((plan, index) => (
            <ListItem
              key={index}
              name={PLAN_TYPE[plan as keyof typeof PLAN_TYPE].title}
              action={() => onCheckboxClick(plan, 'plans')}
              icon={<Checkbox checked={selectedItems['plans']?.includes(plan)} sx={{ p: 0 }} />}
            />
          ))}
        </List>
      );
    }

    if (step === 2) {
      return (
        <>
          <TextField
            id="email-input"
            label={t('sendFormModal.emailAddress')}
            variant="outlined"
            fullWidth
            value={email}
            inputProps={{
              type: 'email',
            }}
            onChange={handleEmailChange}
            error={emailError}
            helperText={emailError ? t('sendFormModal.emailError') : ''}
          />
          <StyledNote>{t('sendFormModal.emailNote')}</StyledNote>
        </>
      );
    }

    if (step === 3) {
      return (
        <>
          <Box display="flex" justifyContent="center" sx={{ mb: 1 }}>
            <FactCheckIcon color="secondary" sx={{ fontSize: '64px' }} />
          </Box>
          <Typography variant="body2" textAlign="center">
            {`${t('sendFormModal.planSent')} ${email}!`}
          </Typography>
        </>
      );
    }
  };

  const buttons = () => {
    if (step === 1) {
      return (
        <Button variant="contained" onClick={() => setStep(2)} disabled={!isMinSelected('plans')}>
          Next
        </Button>
      );
    }

    if (step === 2) {
      return (
        <>
          <Button variant="outlined" onClick={() => setStep(1)}>
            Back
          </Button>
          <Button
            variant="contained"
            startIcon={<SendRoundedIcon />}
            onClick={handleSendBtn}
            disabled={emailError || email.length === 0}
          >
            Send
          </Button>
        </>
      );
    }

    if (step === 3) {
      return (
        <Button variant="contained" onClick={closeSendForm}>
          {t('close')}
        </Button>
      );
    }
  };

  return (
    <Modal
      open={open}
      handleClose={() => handleCloseBtn()}
      header={header()}
      mainContent={content()}
      buttons={buttons()}
      fullWidth
    />
  );
};
